import { useState } from "react";
import { Helmet } from "react-helmet";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

// components
import { Button, Input } from "components/LibraryComponents/Components";
import { VerifiedBusinessesDialog } from "components/Dialogs";

// utils
import { dateFormatter } from "utils/helper";

// store
import useAuthStore from "store/useAuthStore";

// fetch
import { updateExtendedProfileData } from "data/fetch";

// styles
import styles from "./ParticipationAgreement.module.scss";

export default function ParticipationAgreement() {
  const { newClaimedBusinesses } = useAuthStore();

  const navigate = useNavigate();

  const [certifierName, setCertifierName] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isOpen, setIsOpen] = useState<boolean>(false);

  const isDisabled = isLoading || !certifierName.trim();

  const handleSubmit = async () => {
    if (!newClaimedBusinesses) {
      toast.error("No claimed business found!");
      return;
    }

    try {
      setIsLoading(true);

      const payload = {
        participation_agreement: {
          certifier_name: certifierName.trim(),
          date: dateFormatter(new Date().toString(), "MM/DD/YYYY"),
        },
      };

      if (newClaimedBusinesses.length > 1) {
        // If multiple businesses are claimed, send update requests for each business concurrently.
        await Promise.all(
          newClaimedBusinesses.map((business) =>
            updateExtendedProfileData(business.uei, JSON.stringify(payload))
          )
        );

        // show a diloag to pick and edit a business
        setIsOpen(true);
      } else {
        await updateExtendedProfileData(
          newClaimedBusinesses[0].uei,
          JSON.stringify(payload)
        );

        navigate(
          `/profile/${newClaimedBusinesses[0].uei}/${newClaimedBusinesses[0].cage_codes[0]}/edit`
        );
      }
    } catch (error: any) {
      console.error("Error saving certifier name:", error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Participation Agreement | Small Business Search</title>
      </Helmet>

      <div className={styles["participation-agreement-root"]}>
        <h1 className={styles["heading"]}>Participation agreement</h1>

        <div className={styles["main-content"]}>
          <h3 className={styles["sub-heading"]}>
            Small Business Certification Statement
          </h3>
          <p className={styles["desc"]}>
            By submitting this form, I certify that:
          </p>

          <ol className={styles["list"]}>
            <li className={styles["list-item"]}>
              The business is a small business under the size standard(s) for
              the listed NAICS code(s) as provided under 13 C.F.R. Part 121;
            </li>
            <li className={styles["list-item"]}>
              This small business, including its principals with primary
              management responsibilities, has not been debarred or suspended
              from furnishing materials, supplies or services to the Federal
              Government;
            </li>
            <li className={styles["list-item"]}>
              The characteristics of the small business’s ownership are
              accurately reflected in the System for Awards Management (SAM)
              profile;
            </li>
            <li className={styles["list-item"]}>
              I have personally reviewed all of the information provided in the
              profile and believe that it is true and correct;
            </li>
            <li className={styles["list-item"]}>
              I am aware that the Small Business Act provides severe penalties
              on parties who knowingly misrepresent their status as a "small
              business concern" in order to obtain for oneself or another
              certain Federal contracts and subcontracts. Such penalties include
              fines of not more than $500,000 or imprisonment for not more than
              10 years, or both; administrative remedies prescribed by the
              Program Fraud Civil Remedies Act of 1986 (31 U.S.C. 3801-3812);
              and suspension and debarment as specified in subpart 9.4 of title
              48, Code of Federal Regulations (or any successor regulation)(see
              15 U.S.C. 645 16(d)); and
            </li>
            <li className={styles["list-item"]}>
              I am authorized to make this certification on behalf of this small
              business. A red asterisk (*) indicates a required field.
            </li>
          </ol>

          <p className={styles["desc-italic"]}>
            A red asterisk (<span>*</span>) indicates a required field.
          </p>

          <div className={styles["content"]}>
            <Input
              id="name"
              name="name"
              type="text"
              label="Certifier name"
              suffix={<span className={styles["asterisk"]}> *</span>}
              placeholder="Type your first and last name here"
              value={certifierName}
              onChange={(e) => setCertifierName(e.target.value)}
            />

            <div className={styles["group"]}>
              <p className={styles["label"]}>Certification date</p>
              <p className={styles["value"]}>
                {dateFormatter(new Date().toDateString(), "MM/DD/YYYY")}
              </p>
            </div>

            <Button
              type="submit"
              className={styles["btn-submit"]}
              disabled={isDisabled}
              loading={isLoading}
              onClick={handleSubmit}
            >
              {isLoading ? "Saving..." : "Save and continue"}
            </Button>
          </div>
        </div>
      </div>

      {isOpen && (
        <VerifiedBusinessesDialog
          businessess={newClaimedBusinesses}
          open={isOpen}
          setOpen={setIsOpen}
        />
      )}
    </>
  );
}
